import { useState, useEffect, useCallback } from 'react';
import { CircleCheck, CircleX, FileQuestionMark, RotateCcw, Copy } from 'lucide-react';
import { api } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { useAuth } from '@/lib/AuthContext';
import { invalidateConsistency } from '@/lib/consistencyInvalidation';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { SharePointAccessModal } from '@/components/shared/SharePointAccessModal';
import { SectionPicker } from './SectionPicker';
import { AcknowledgeAllDuplicatesDialog } from './AcknowledgeAllDuplicatesDialog';
import { CTD_SECTIONS } from '@/lib/ctdSections';
import { Telemetry } from '@/lib/telemetry';
import type { UnclassifiedFile, DuplicateGroupOut } from '@/types/api';

type ConflictTab = 'missing' | 'duplicates';

function isGroupAcknowledged(group: DuplicateGroupOut): boolean {
  return group.documents.every((d) => d.duplicateAcknowledged);
}

export function ManageConflictsPage() {
  const { activeProject, refreshConflictCount } = useProject();
  const { user } = useAuth();
  const [files, setFiles] = useState<UnclassifiedFile[]>([]);
  const [dupGroups, setDupGroups] = useState<DuplicateGroupOut[]>([]);
  /** Groups resolved via "Keep Selected" that the backend no longer returns as duplicates. */
  const [resolvedKeepGroups, setResolvedKeepGroups] = useState<DuplicateGroupOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ConflictTab>('missing');
  const [selectedKeep, setSelectedKeep] = useState<Record<string, Set<string>>>({});
  const [spoModalUrl, setSpoModalUrl] = useState<string | null>(null);
  // A2F-651: bulk-acknowledge dialog state
  const [bulkAckOpen, setBulkAckOpen] = useState(false);
  const [bulkAckSubmitting, setBulkAckSubmitting] = useState(false);
  const [bulkAckError, setBulkAckError] = useState('');

  const loadFiles = useCallback(async () => {
    if (!activeProject) return;
    try {
      const data = await api.projects.unclassified(activeProject.id);
      setFiles(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
  }, [activeProject]);

  const loadDuplicates = useCallback(async () => {
    if (!activeProject) return;
    try {
      // Always include acknowledged so they stay visible
      const data = await api.projects.duplicates(activeProject.id, true);
      setDupGroups(data);
      // Remove any locally-tracked resolved groups that the backend now returns
      // (e.g. if re-ingest recreated the duplicate)
      const backendKeys = new Set(data.map((g) => `${g.submissionId}:${g.ectdSection}`));
      setResolvedKeepGroups((prev) =>
        prev.filter((g) => !backendKeys.has(`${g.submissionId}:${g.ectdSection}`))
      );
    } catch (e: any) {
      setError(e.message || 'Failed to load duplicates');
    }
  }, [activeProject]);

  useEffect(() => {
    if (!activeProject) return;
    setLoading(true);
    setResolvedKeepGroups([]);
    Promise.all([loadFiles(), loadDuplicates()]).finally(() => setLoading(false));
  }, [activeProject, loadFiles, loadDuplicates]);

  // --- Missing Section handlers ---

  const handleClassify = async (fileId: string, section: string) => {
    if (!activeProject) return;
    setBusyId(fileId);
    try {
      const result = await api.projects.classify(activeProject.id, fileId, section);
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, resolved: true, resolvedSection: result.section, resolvedModule: `M${result.section.split('.')[0]}` }
            : f
        )
      );
      refreshConflictCount();
      // A2F-570: backend cleared scan caches server-side; nudge Dashboard/
      // Plan/Review to refetch so tier classification picks up the new Document.
      invalidateConsistency(activeProject.id);
    } catch (e: any) {
      setError(e.message || 'Failed to classify');
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (fileId: string) => {
    if (!activeProject) return;
    setBusyId(fileId);
    try {
      await api.projects.dismissUnclassified(activeProject.id, fileId);
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, resolved: true, resolvedSection: null, resolvedModule: null }
            : f
        )
      );
      refreshConflictCount();
    } catch (e: any) {
      setError(e.message || 'Failed to dismiss');
    } finally {
      setBusyId(null);
    }
  };

  const handleReopen = async (fileId: string) => {
    if (!activeProject) return;
    setBusyId(fileId);
    try {
      await api.projects.reopenUnclassified(activeProject.id, fileId);
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? { ...f, resolved: false, resolvedSection: null, resolvedModule: null }
            : f
        )
      );
      refreshConflictCount();
    } catch (e: any) {
      setError(e.message || 'Failed to reopen');
    } finally {
      setBusyId(null);
    }
  };

  // --- Open-in-Word handler (A2F-581) ---

  // Mirror of SubmissionExplorerPage.openInWord — same SSO/modal gating via
  // SharePointAccessModal; errors silently (routine "file moved/closed" case).
  const openInWord = useCallback(async (documentId: string, fileType?: string) => {
    Telemetry.documentOpenedInWord({ document_id: documentId, file_type: fileType ?? 'unknown' });
    try {
      const res = await api.documents.openLocal(documentId);
      if (res.status === 'redirect' && res.webUrl) {
        if (user?.authMethod === 'sso') {
          window.open(res.webUrl, '_blank');
        } else {
          setSpoModalUrl(res.webUrl);
        }
      }
    } catch { /* ignore — routine "file moved/closed" case, no scary banner */ }
  }, [user?.authMethod]);

  // --- Duplicate handlers ---

  const groupKey = (g: DuplicateGroupOut) => `${g.submissionId}:${g.ectdSection}`;

  const toggleKeep = (gk: string, docId: string) => {
    setSelectedKeep((prev) => {
      const current = new Set(prev[gk] || []);
      if (current.has(docId)) current.delete(docId);
      else current.add(docId);
      return { ...prev, [gk]: current };
    });
  };

  const handleResolve = async (group: DuplicateGroupOut) => {
    if (!activeProject) return;
    const gk = groupKey(group);
    const keepIds = Array.from(selectedKeep[gk] || []);
    if (keepIds.length === 0) {
      setError('Select at least one file to keep');
      return;
    }
    setBusyId(gk);
    try {
      await api.projects.resolveDuplicates(activeProject.id, keepIds);
      // Build a synthetic resolved group from the kept docs
      const keepSet = new Set(keepIds);
      const keptDocs = group.documents
        .filter((d) => keepSet.has(d.id))
        .map((d) => ({ ...d, duplicateAcknowledged: true }));
      const resolvedGroup: DuplicateGroupOut = {
        ...group,
        documents: keptDocs,
      };
      // Acknowledge the remaining docs on the backend (works even for single docs)
      await api.projects.acknowledgeDuplicates(activeProject.id, group.submissionId, group.ectdSection).catch(() => {});
      await Promise.all([loadFiles(), loadDuplicates()]);
      // If the backend no longer returns this group (resolved to <=1 doc), track it locally
      setResolvedKeepGroups((prev) => {
        const existing = prev.filter((g) => groupKey(g) !== gk);
        return [...existing, resolvedGroup];
      });
      setSelectedKeep((prev) => {
        const next = { ...prev };
        delete next[gk];
        return next;
      });
      refreshConflictCount();
    } catch (e: any) {
      setError(e.message || 'Failed to resolve duplicates');
    } finally {
      setBusyId(null);
    }
  };

  const handleAcknowledge = async (group: DuplicateGroupOut) => {
    if (!activeProject) return;
    const gk = groupKey(group);
    setBusyId(gk);
    try {
      await api.projects.acknowledgeDuplicates(activeProject.id, group.submissionId, group.ectdSection);
      await loadDuplicates();
      refreshConflictCount();
    } catch (e: any) {
      setError(e.message || 'Failed to acknowledge duplicates');
    } finally {
      setBusyId(null);
    }
  };

  // A2F-651: bulk-acknowledge every unresolved duplicate group in one gesture.
  // Backend wraps the whole batch in a single transaction with a single audit
  // event. Refetches the duplicates list on success so the UI reflects the
  // shifted state without an F5 (mirrors the A2F-589 reactivity fix).
  const handleAcknowledgeAll = async () => {
    if (!activeProject) return;
    setBulkAckSubmitting(true);
    setBulkAckError('');
    try {
      await api.projects.acknowledgeAllDuplicates(activeProject.id);
      await loadDuplicates();
      refreshConflictCount();
      setBulkAckOpen(false);
    } catch (e: any) {
      setBulkAckError(e.message || 'Failed to acknowledge duplicates');
    } finally {
      setBulkAckSubmitting(false);
    }
  };

  const handleUnacknowledge = async (group: DuplicateGroupOut, isLocal: boolean) => {
    if (!activeProject) return;
    const gk = groupKey(group);
    setBusyId(gk);
    try {
      if (isLocal) {
        // Just remove from local state — the demoted files are already in unclassified
        setResolvedKeepGroups((prev) => prev.filter((g) => groupKey(g) !== gk));
      } else {
        await api.projects.unacknowledgeDuplicates(activeProject.id, group.submissionId, group.ectdSection);
        await loadDuplicates();
      }
      refreshConflictCount();
    } catch (e: any) {
      setError(e.message || 'Failed to reopen duplicates');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <LoadingSpinner message="Loading conflicts..." />;

  const unresolved = files.filter((f) => !f.resolved);
  const resolved = files.filter((f) => f.resolved);
  const missingCount = unresolved.length;

  const unresolvedDups = dupGroups.filter((g) => !isGroupAcknowledged(g));
  // Merge backend-acknowledged groups with locally-tracked resolved groups
  const backendAcknowledged = dupGroups.filter((g) => isGroupAcknowledged(g));
  const backendAckKeys = new Set(backendAcknowledged.map((g) => groupKey(g)));
  const localOnly = resolvedKeepGroups.filter((g) => !backendAckKeys.has(groupKey(g)));
  const allAcknowledgedDups = [...backendAcknowledged, ...localOnly];
  const dupCount = unresolvedDups.length;

  const truncatePath = (path: string) => {
    const parts = path.replace(/\\/g, '/').split('/');
    if (parts.length <= 4) return parts.join('/');
    return '.../' + parts.slice(-3).join('/');
  };

  const hasAnyDup = dupGroups.length > 0 || resolvedKeepGroups.length > 0;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div>
          <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
            Settings
          </p>
          <h1 className="text-xl font-bold text-brand-charcoal mt-1">Manage Conflicts</h1>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-6 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('missing')}
          className={`pb-2.5 text-sm font-semibold transition-colors ${
            activeTab === 'missing'
              ? 'text-brand-orange border-b-2 border-brand-orange'
              : 'text-slate-400 hover:text-slate-600'
          }`}
        >
          Missing Section{missingCount > 0 ? ` (${missingCount})` : ''}
        </button>
        <button
          onClick={() => setActiveTab('duplicates')}
          className={`pb-2.5 text-sm font-semibold transition-colors ${
            activeTab === 'duplicates'
              ? 'text-brand-orange border-b-2 border-brand-orange'
              : 'text-slate-400 hover:text-slate-600'
          }`}
        >
          Duplicates{dupCount > 0 ? ` (${dupCount})` : ''}
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <CircleX className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ==================== Missing Section Tab ==================== */}
      {activeTab === 'missing' && (
        <>
          {/* Empty State */}
          {files.length === 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-12">
              <div className="text-center space-y-3">
                <CircleCheck className="w-12 h-12 text-emerald-400 mx-auto" />
                <h2 className="text-lg font-semibold text-brand-charcoal">All documents have been classified</h2>
                <p className="text-sm text-slate-500">
                  No unclassified files found. New conflicts will appear here after re-ingesting.
                </p>
              </div>
            </div>
          )}

          {/* Unresolved Files */}
          {unresolved.length > 0 && (
            <div className="space-y-2">
              {unresolved.map((f) => (
                <div key={f.id} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-start gap-3">
                    <FileQuestionMark className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-brand-charcoal truncate">{f.fileName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">
                          Sub {f.sequenceNumber}
                        </span>
                        <span className="text-xs text-slate-400 truncate" title={f.filePath}>
                          {truncatePath(f.filePath)}
                        </span>
                      </div>
                      {f.candidateSections && f.candidateSections.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                            Suggested:
                          </span>
                          {f.candidateSections.map((cs) => (
                            <button
                              key={cs}
                              onClick={() => handleClassify(f.id, cs)}
                              disabled={busyId === f.id}
                              className="px-2 py-0.5 text-xs font-medium text-brand-orange bg-orange-50 border border-orange-200 rounded hover:bg-orange-100 transition-colors disabled:opacity-40"
                              title={CTD_SECTIONS[cs] || cs}
                            >
                              {cs}
                              {CTD_SECTIONS[cs] ? ` ${CTD_SECTIONS[cs]}` : ''}
                            </button>
                          ))}
                        </div>
                      )}
                      <SectionPicker
                        onAssign={(section) => handleClassify(f.id, section)}
                        onDismiss={() => handleDismiss(f.id)}
                        disabled={busyId === f.id}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Previously Resolved */}
          {resolved.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between mt-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Previously Resolved
                </p>
                <p className="text-xs text-emerald-600 font-medium">
                  {resolved.length} file{resolved.length !== 1 ? 's' : ''} mapped
                </p>
              </div>
              {resolved.map((f) => {
                const sectionTitle = f.resolvedSection ? CTD_SECTIONS[f.resolvedSection] || f.resolvedSection : null;
                const isDismissed = !f.resolvedSection;

                return (
                  <div key={f.id} className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-start gap-3">
                      <CircleCheck className={`w-5 h-5 mt-0.5 shrink-0 ${isDismissed ? 'text-slate-300' : 'text-emerald-400'}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-brand-charcoal truncate">{f.fileName}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">
                            Sub {f.sequenceNumber}
                          </span>
                          {isDismissed ? (
                            <span className="text-xs text-slate-400 italic">Dismissed</span>
                          ) : (
                            <span className="text-xs text-emerald-600">
                              Mapped: {f.resolvedModule} &gt; {f.resolvedSection} {sectionTitle}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleReopen(f.id)}
                        disabled={busyId === f.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 rounded-lg border border-slate-200 hover:bg-slate-50 hover:text-slate-700 transition-colors disabled:opacity-40 shrink-0"
                      >
                        <RotateCcw className="w-3 h-3" />
                        Re-assign
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* All resolved message */}
          {files.length > 0 && unresolved.length === 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-700 flex items-center gap-2">
              <CircleCheck className="w-4 h-4 shrink-0" />
              All conflicts have been resolved. Use Re-assign to change any mapping.
            </div>
          )}
        </>
      )}

      {/* ==================== Duplicates Tab ==================== */}
      {activeTab === 'duplicates' && (
        <>
          {/* Empty State — no duplicate groups at all */}
          {!hasAnyDup && (
            <div className="bg-white rounded-xl border border-slate-200 p-12">
              <div className="text-center space-y-3">
                <CircleCheck className="w-12 h-12 text-emerald-400 mx-auto" />
                <h2 className="text-lg font-semibold text-brand-charcoal">No duplicate documents</h2>
                <p className="text-sm text-slate-500">
                  Each CTD section has at most one document per submission. Duplicates will appear here after re-ingesting.
                </p>
              </div>
            </div>
          )}

          {/* A2F-651: bulk-acknowledge all unresolved duplicate groups in
              one gesture. Hidden when zero unresolved groups; shows precise
              count so the user knows what they're committing to. */}
          {unresolvedDups.length > 0 && (
            <div className="flex items-center justify-end -mt-2">
              <button
                onClick={() => { setBulkAckError(''); setBulkAckOpen(true); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-orange border border-brand-orange rounded-lg hover:bg-orange-50 transition-colors"
              >
                <CircleCheck className="w-3.5 h-3.5" />
                Acknowledge all {unresolvedDups.length} {unresolvedDups.length === 1 ? 'conflict' : 'conflicts'}
              </button>
            </div>
          )}

          {/* Unresolved duplicate groups */}
          {unresolvedDups.length > 0 && (
            <div className="space-y-4">
              {unresolvedDups.map((group) => {
                const gk = groupKey(group);
                const kept = selectedKeep[gk] || new Set<string>();
                const isBusy = busyId === gk;

                return (
                  <div key={gk} className="bg-white rounded-xl border border-slate-200 p-5">
                    {/* Group Header */}
                    <div className="flex items-center gap-3 mb-4">
                      <Copy className="w-5 h-5 text-orange-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-brand-charcoal">
                          {group.ectdSection} — {group.sectionTitle}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">
                            Sub {group.sequenceNumber}
                          </span>
                          <span className="text-xs text-slate-400">
                            {group.module}
                          </span>
                          <span className="text-xs text-orange-500 font-medium">
                            {group.documents.length} files
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* File List with Checkboxes */}
                    <div className="space-y-2 mb-4">
                      {group.documents.map((doc) => (
                        // A2F-581: HTML forbids interactive descendants (<button>) inside
                        // <label>, so we replace the wrapper with a <div> that preserves
                        // click-anywhere-to-toggle via onClick, while the filename gets its
                        // own <button> with stopPropagation so opening the file does NOT
                        // toggle keep. aria-label on the checkbox restores the accessible name.
                        <div
                          key={doc.id}
                          onClick={() => toggleKeep(gk, doc.id)}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={kept.has(doc.id)}
                            onChange={() => toggleKeep(gk, doc.id)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Keep ${doc.fileName}`}
                            className="w-4 h-4 rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30"
                          />
                          <div className="min-w-0 flex-1">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openInWord(doc.id, doc.fileType); }}
                              title={`Open ${doc.fileName} in Word`}
                              className="block w-full text-left text-sm text-brand-charcoal hover:text-brand-orange hover:underline truncate"
                            >
                              {doc.fileName}
                            </button>
                            <p className="text-[10px] text-slate-400 truncate" title={doc.filePath}>
                              {truncatePath(doc.filePath)}
                            </p>
                          </div>
                          <span className="text-[10px] text-slate-400 uppercase shrink-0">{doc.fileType}</span>
                        </div>
                      ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleResolve(group)}
                        disabled={isBusy || kept.size === 0}
                        className="px-4 py-2 text-xs font-semibold text-white bg-brand-orange rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-40"
                      >
                        Keep Selected
                      </button>
                      <button
                        onClick={() => handleAcknowledge(group)}
                        disabled={isBusy}
                        className="px-4 py-2 text-xs font-semibold text-brand-orange border border-brand-orange rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-40"
                      >
                        Keep All
                      </button>
                      {kept.size > 0 && (
                        <span className="text-[10px] text-slate-400">
                          {kept.size} selected, {group.documents.length - kept.size} will be demoted
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Acknowledged / resolved duplicate groups */}
          {allAcknowledgedDups.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mt-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
                  Previously Resolved
                </p>
                <p className="text-xs text-emerald-600 font-medium">
                  {allAcknowledgedDups.length} group{allAcknowledgedDups.length !== 1 ? 's' : ''} acknowledged
                </p>
              </div>
              {allAcknowledgedDups.map((group) => {
                const gk = groupKey(group);
                const isBusy = busyId === gk;
                const isLocal = localOnly.some((g) => groupKey(g) === gk);
                const keptCount = group.documents.length;

                return (
                  <div key={gk} className="bg-white rounded-xl border border-slate-200 p-5">
                    {/* Group Header */}
                    <div className="flex items-start gap-3">
                      <CircleCheck className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-brand-charcoal">
                          {group.ectdSection} — {group.sectionTitle}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">
                            Sub {group.sequenceNumber}
                          </span>
                          <span className="text-xs text-slate-400">
                            {group.module}
                          </span>
                          <span className="text-xs text-emerald-600 font-medium">
                            Keeping {keptCount} file{keptCount !== 1 ? 's' : ''}
                          </span>
                        </div>

                        {/* File list — compact, no checkboxes */}
                        <div className="mt-3 space-y-1">
                          {group.documents.map((doc) => (
                            <div
                              key={doc.id}
                              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50/50 border border-emerald-100"
                            >
                              <CircleCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                              <button
                                type="button"
                                onClick={() => openInWord(doc.id, doc.fileType)}
                                title={`Open ${doc.fileName} in Word`}
                                className="text-sm text-brand-charcoal hover:text-brand-orange hover:underline truncate flex-1 text-left"
                              >
                                {doc.fileName}
                              </button>
                              <span className="text-[10px] text-slate-400 uppercase shrink-0">{doc.fileType}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <button
                        onClick={() => handleUnacknowledge(group, isLocal)}
                        disabled={isBusy}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 rounded-lg border border-slate-200 hover:bg-slate-50 hover:text-slate-700 transition-colors disabled:opacity-40 shrink-0"
                      >
                        <RotateCcw className="w-3 h-3" />
                        Re-assign
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* All resolved message */}
          {hasAnyDup && unresolvedDups.length === 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-700 flex items-center gap-2">
              <CircleCheck className="w-4 h-4 shrink-0" />
              All duplicate conflicts have been resolved. Use Re-assign to change any decision.
            </div>
          )}
        </>
      )}

      {/* A2F-651: bulk-acknowledge confirmation dialog. Mounted at root so
          the overlay covers the whole page. */}
      {bulkAckOpen && (
        <AcknowledgeAllDuplicatesDialog
          count={unresolvedDups.length}
          submitting={bulkAckSubmitting}
          error={bulkAckError}
          onConfirm={handleAcknowledgeAll}
          onClose={() => { if (!bulkAckSubmitting) setBulkAckOpen(false); }}
        />
      )}

      {/* A2F-581: SPO access modal — shown when a non-SSO user clicks a duplicate
          filename whose open endpoint returns a SharePoint redirect URL. Mirrors
          the SubmissionExplorerPage / SubmissionViewerPage pattern exactly. */}
      {spoModalUrl && (
        <SharePointAccessModal webUrl={spoModalUrl} onClose={() => setSpoModalUrl(null)} />
      )}
    </div>
  );
}
